import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link, Button, Grid } from '@material-ui/core';

import { SectionCard, DetailsList, Chip, LoadingSpinner, ErrorComponent } from '../../../../components';
import { userLanguage } from '../../../../core/AuthContext';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';

import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { Exception } from '@jewelutions/core-common-domain';
import PlatformMerchantContractEditDialog from '../PlatformMerchantContractEditDialog';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

const PlatformMerchantContractDetails: React.FC = () => {
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [errorComponentState, setErrorComponentState] = useState(false);
  const merchantContractId = PlatformNavigation.platformMerchantContractDetails.paramsFromPath(
    useRouteMatch()
  ).merchantContractId;
  const history = useHistory();
  const { t: translate } = useTranslation();

  const {
    data,
    loading,
    error,
    refetch
  } = GQLOperationBuilder.platformSaleCatalog.getPlatformSaleCatalogWithPlatformMerchantContracts({
    includeOnlyActiveContracts: true
  });

  const reloadData = async () => {
    refetch();
  };

  if (loading) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={refetch}
        message={error.message}
      />
    );

  const currentMerchantContract = GQLOperationBuilder.platformSaleCatalog
    .platformSaleCatalogWithPlatformMerchantContractArrayFromResponse(data)
    .find(merchantContract => merchantContract?.platformMerchantContract?.id === merchantContractId);

  if (currentMerchantContract === undefined || currentMerchantContract === null)
    throw new Exception(`Cannot find merchant contract ${merchantContractId}`);

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Overview" fitContent>
          <DetailsList
            list={[
              {
                primary: 'Producer Party Name',
                secondary: (
                  <Link
                    onClick={() => {
                      history.push(
                        PlatformNavigation.partyDetails.pathFromParams({
                          partyId: currentMerchantContract.platformMerchantContract!.producerParty.id
                        })
                      );
                    }}
                  >
                    {currentMerchantContract.platformMerchantContract!.producerParty.name}
                  </Link>
                )
              },
              {
                primary: 'Producer Delivery Unit',
                secondary: currentMerchantContract.platformMerchantContract!.producerDeliveryUnit.name
              },
              {
                primary: 'Merchant Party Name',
                secondary: (
                  <Link
                    onClick={() => {
                      history.push(
                        PlatformNavigation.partyDetails.pathFromParams({
                          partyId: currentMerchantContract.platformMerchantContract!.merchantParty.id
                        })
                      );
                    }}
                  >
                    {currentMerchantContract.platformMerchantContract!.merchantParty.name}
                  </Link>
                )
              },
              {
                primary: 'Platform Sale Catalog Name',
                secondary: (
                  <Link
                    onClick={() => {
                      history.push(
                        PlatformNavigation.platformSaleCatalogDetails.pathFromParams({
                          platformSaleCatalogId: currentMerchantContract.platformSaleCatalog.id
                        })
                      );
                    }}
                  >
                    {currentMerchantContract.platformSaleCatalog!.name[userLanguage]}
                  </Link>
                )
              },
              {
                primary: 'Platform Sale Catalog Description',
                secondary: currentMerchantContract.platformSaleCatalog!.description[userLanguage] + ''
              },
              {
                primary: 'Platform Merchant Contract Active',
                secondary: (
                  <Chip
                    size="small"
                    mr={1}
                    mb={1}
                    label={currentMerchantContract.platformMerchantContractActive ? 'YES' : 'NO'}
                    $active={currentMerchantContract.platformMerchantContractActive}
                  />
                )
              },
              {
                primary: 'Started At',
                secondary:
                  currentMerchantContract.platformMerchantContract!.startsAt &&
                  new Date(
                    Date.parse(currentMerchantContract.platformMerchantContract!.startsAt)
                  ).toLocaleString()
              },
              {
                primary: 'Ends At',
                secondary:
                  currentMerchantContract.platformMerchantContract!.endsAt &&
                  new Date(
                    Date.parse(currentMerchantContract.platformMerchantContract!.endsAt)
                  ).toLocaleString()
              },
              {
                primary: 'Updated At',
                secondary: new Date(
                  Date.parse(currentMerchantContract.platformMerchantContract!.updatedAt)
                ).toLocaleString()
              }
            ]}
          />
        </SectionCard>
      </Grid>
      <Button
        variant={'contained'}
        color={'primary'}
        onClick={() => {
          setShowEditDialog(true);
        }}
        style={{ marginLeft: '4px' }}
      >
        Change sale Catalog
      </Button>

      {showEditDialog && (
        <PlatformMerchantContractEditDialog
          merchantContract={currentMerchantContract}
          onClose={success => {
            history.push(PlatformNavigation.platformMerchantContracts.pathFromParams({}));
          }}
        ></PlatformMerchantContractEditDialog>
      )}
    </Fragment>
  );
};

export default PlatformMerchantContractDetails;
